import { useAnalyticsTracking } from '@/analytics/snowplow/AnalyticsTrackingContext';
import type { Design_systemUiEventEventTypeV1, Entity } from '@montugroup/data-collection';
import { emitUIEvent } from '@montugroup/data-collection';
import { merge } from 'lodash';
import type { TypedAnalyticsContexts } from './analytics-context.types';
import { contextSchemas } from './analytics-context.types';

/**
 * `useEmitUiEvent` is a hook for emitting Snowplow UI events with analytics context.
 *
 * It pulls the current context from `AnalyticsTrackingProvider` and
 * merges it with any `extraContext` you pass in. The merged result is then used
 * to attach relevant entities (e.g. consultation, feature flags) to the emitted event.
 *
 * Only keys defined in `contextSchemas` will be included in the emitted context.
 * Any unknown keys in `extraContext` will be ignored silently.
 *
 * Note: The `uiEvent` key in the context is NOT merged into Snowplow contexts.
 * It's only used for components to access the `context_id` via `context_id`.
 * This prevents duplicate `ui_event` contexts in Snowplow events.
 *
 * This is useful when you want to enrich a single event with context from the provider
 *
 * Example:
 * ```tsx
 * const emitEvent = useEmitUiEvent();
 * emitEvent(
 *   {
 *     component_name: 'ConsentRequestButton',
 *     event_name: 'send_request_consent',
 *     id: 'send_request_btn',
 *     value: 'Send Request'
 *   },
 *   {
 *     consultation: {
 *       ...ctx.consultation,
 *       consultation_id: '123'
 *     }
 *   }
 * );
 * ```
 */

export const useEmitUiEvent = () => {
  const context = useAnalyticsTracking();

  return (event: Design_systemUiEventEventTypeV1, extraContext?: TypedAnalyticsContexts) => {
    const merged = merge({}, context, extraContext);

    const contexts = Object.entries(merged).flatMap(([key, value]) =>
      value && key !== 'uiEvent'
        ? [{ schema: contextSchemas[key as keyof typeof contextSchemas], data: value as Entity }]
        : []
    );

    emitUIEvent(event, contexts);
  };
};
